import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";
import Checkbox from "@mui/material/Checkbox";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import NumbersIcon from "@mui/icons-material/Numbers";
import EditIcon from "@mui/icons-material/Edit";
import UploadIcon from "@mui/icons-material/Upload";

import toast from "react-hot-toast";

import CategoryContext from "context/category-context";

import api from "services/api";
import moment from "moment";
import { Upload } from "antd";

import { materialCategoryOptions } from "utils/data";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import Tooltip from "@mui/material/Tooltip";
import { UserSettingContext } from "context/user-setting";
import DataTable from "examples/Tables/DataTable";

const ImportMaterialModal = ({ visible, handleClose, handleOk, handleRefresh = () => {} }) => {
  const [data, setData] = useState([]);

  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [customizedColumns, setCustomizedColumns] = useState([]);

  const { setting } = useContext(UserSettingContext);

  useEffect(() => {
    retrieveMaterial();
  }, []);

  useEffect(() => {
    setCustomizedColumns(setting?.materialLibrary?.fields || []);
  }, [setting]);

  const retrieveMaterial = async () => {
    try {
      const { data } = await api.materialLibrary.list({
        order: "-updated_at",
      });
      setData(data?.materials || []);
    } catch (err) {
      toast.error(`Retrieve material list failed!`);
      console.log(err);
    }
  };

  const getMaterialTableRow = (item) => {
    const cate = materialCategoryOptions.find((x) => x.value == item?.category);
    const images = item?.metadata?.images || [];

    let rowMap = {
      key: item.id,
      name: item?.name,
      category: cate ? cate.label : item?.category,
      code: item?.code,
      images: images,
      description: item?.metadata?.description,
      created_at: item.created_at,
      updated_at: item.updated_at,
      action: item,
    };

    for (let i = 0; i < customizedColumns.length; i++) {
      let field = customizedColumns[i]?.field;
      if (field) {
        rowMap[field] = item?.metadata?.[field];
      }
    }

    return rowMap;
  };

  const columns = [
    {
      Header: "Image",
      accessor: "images",
      align: "center",
      Cell: ({ row, value }) => {
        return (
          <SuiBox display="flex" alignItems="center" justifyContent="space-between">
            <Checkbox
              checked={selectedMaterials.findIndex((x) => x.key == row?.original.key) > -1}
            />
            {value && value.length > 0 ? (
              <ImageCardWrapper data={value[0]} width={50} height={50} />
            ) : (
              <SuiBox minWidth={50} minHeight={50} />
            )}
          </SuiBox>
        );
      },
    },
    {
      Header: "Name",
      accessor: "name",
      align: "center",
    },
    {
      Header: "Category",
      accessor: "category",
    },
    {
      Header: "Code",
      accessor: "code",
    },
  ];

  const lastColumns = [
    {
      Header: "Description",
      accessor: "description",
      Cell: ({ row, value }) => {
        return (
          <Tooltip title={value}>
            <div
              style={{
                wordBreak: "break-word",
                maxWidth: 200,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: "1",
                WebkitBoxOrient: "vertical",
              }}
            >
              {value}
            </div>
          </Tooltip>
        );
      },
    },
  ];

  const addedColumns = customizedColumns.map((x) => ({
    Header: x.headerName,
    accessor: x.field,
  }));

  const allColumns = [...columns, ...addedColumns, ...lastColumns];

  const refresh = () => {
    retrieveMaterial();
  };

  const rows = data.map((item) => getMaterialTableRow(item));

  const handleRowClick = (row) => {
    const itemId = row?.original.key;
    setSelectedMaterials((prev) => {
      const findIndex = prev.findIndex((x) => x.key == itemId);
      if (findIndex > -1) {
        prev.splice(findIndex, 1);
        return [...prev];
      } else {
        let newItems = [...prev, row?.original];
        return newItems;
      }
    });
  };

  return (
    <Dialog
      open={visible}
      onClose={(e, reason) => {
        if (reason != "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="xl"
    >
      <DialogTitle id="alert-dialog-title">
        <SuiBox display="flex" gap={1}>
          Material Library
        </SuiBox>

        <IconButton
          aria-label="close"
          onClick={() => handleClose()}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <DataTable
            table={{ columns: allColumns, rows: rows }}
            entriesPerPage={{ defaultValue: 20 }}
            noEndBorder
            handleRowClick={handleRowClick}
            canSearch
          />
          <SuiBox
            pt={3}
            px={3}
            display="flex"
            justifyContent="end"
            alignItems={{ xs: "flex-start", sm: "center" }}
            flexDirection={{ xs: "column", sm: "row" }}
          >
            <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
              <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                <SuiButton variant="gradient" size="medium" onClick={handleClose} color="light">
                  Cancel
                </SuiButton>
              </SuiBox>
            </SuiBox>
            <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
              <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                <SuiButton
                  size="medium"
                  variant="gradient"
                  color="dark"
                  onClick={() => {
                    handleOk(selectedMaterials, customizedColumns);
                    handleClose();
                  }}
                >
                  Import
                </SuiButton>
              </SuiBox>
            </SuiBox>
          </SuiBox>
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
};

export default ImportMaterialModal;
